import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedOverlayPositionChange, ConnectionPositionPair, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { BehaviorSubject } from 'rxjs';
import { camelToSnakeCase, DEFAULT_TOOLTIP_POSITIONS, getPlacementName, POSITION_MAP } from '../dropdown/overlay-position';
import { PhxTriggerType } from './phx-tooltip.directive';
import { Cross } from './../icon/icon-map';
import { WHITE } from '../../helpers/colors';
import { animate, style, transition, trigger } from '@angular/animations';
import { PhxTooltipFixedWidth } from './phx-tooltip.types';

export type StringTemplateRef = string | TemplateRef<void> | null;

export type PhxTooltipSize = 'extra-small' | 'small' | 'medium';

export type PhxTooltipIntent = 'accent' | 'neutral' | 'positive' | 'negative' | 'warning' | 'caution';

const PHX_MAX_WIDTH_DEFAULT_VALUE = 240;
const PHX_MAX_WIDTH_MIN_VALUE = 120;
const PHX_MAX_WIDTH_MAX_VALUE = 560;

@Component({
  selector: 'phx-tooltip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './phx-tooltip.component.html',
  styleUrls: ['./phx-tooltip.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.8)' }),
        animate('0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [animate('0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)', style({ opacity: 0, transform: 'scale(0.8)' }))]),
    ]),
  ],
})
export class PhxTooltipComponent {
  public titleIsTemplate: boolean;
  public subtitleIsTemplate: boolean;
  public contentIsTemplate: boolean;
  public supportedPositions: ConnectionPositionPair[] = [...DEFAULT_TOOLTIP_POSITIONS];
  public readonly closeIcon: string = Cross;
  // TODO: replace with css token when available
  public readonly closeIconColor: string = WHITE;
  @ViewChild('overlay', { static: false }) overlay!: CdkConnectedOverlay;
  @ViewChild('tooltipContent', { static: false }) tooltipContent!: ElementRef<HTMLElement>;
  public origin: CdkOverlayOrigin;
  public classMap: Map<string, any> = new Map();
  public overlayClassName: string;
  public mouseState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public outsideClick$: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  public scrollStrategy: ScrollStrategy;

  private prefixClass = 'phx-tooltip';
  private _title: StringTemplateRef;
  private _subtitle: StringTemplateRef;
  private _content: StringTemplateRef;
  private _visible = false;
  private _placement = 'top';
  private _originalPlacement = 'top';
  private _trigger: PhxTriggerType = 'hover';
  private _size: PhxTooltipSize = 'medium';
  private _fixedWidth: PhxTooltipFixedWidth;
  private _maxWidth: number = PHX_MAX_WIDTH_DEFAULT_VALUE;
  private _actionsMaxHeight: number;
  private _intent: PhxTooltipIntent = 'neutral';
  private _showBackdrop: boolean = false;
  private _phxTooltipVisibleChange: EventEmitter<boolean>;
  private _showCloseIcon: boolean = false;
  private _closeIconClicked: EventEmitter<void>;
  private _tooltipContext: TemplateRef<any>;
  private _offsetX: number = 0;
  private _offsetY: number = 0;
  private _paddingNone: boolean = false;
  private _isToggleTipVisible: boolean = false;
  private _isToggleTip: boolean = false;
  private _toggleTipActions: HTMLDivElement | null = null;
  private static isEmpty(title: StringTemplateRef, subtitle: StringTemplateRef, content: StringTemplateRef): boolean {
    return (
      (title instanceof TemplateRef ? false : Boolean(!title)) &&
      (subtitle instanceof TemplateRef ? false : Boolean(!subtitle)) &&
      (content instanceof TemplateRef ? false : Boolean(!content))
    );
  }

  public set title(value: StringTemplateRef) {
    this._title = value;
    this.titleIsTemplate = value instanceof TemplateRef;
  }
  public get title(): StringTemplateRef {
    return this._title;
  }

  public set subtitle(value: StringTemplateRef) {
    this._subtitle = value;
    this.subtitleIsTemplate = value instanceof TemplateRef;
  }
  public get subtitle(): StringTemplateRef {
    return this._subtitle;
  }

  public set content(value: StringTemplateRef) {
    this._content = value;
    this.contentIsTemplate = value instanceof TemplateRef;
  }
  
  public get content(): StringTemplateRef {
    return this._content;
  }

  public set placement(value: string) {
    if (value !== this._placement) {
      this._placement = value;
      // Store the original placement when it's explicitly set
      // This way we know what the user's preference was
      this._originalPlacement = value;
      // Instead of adding to supported positions, we'll update them with offset
      this.updatePositions();
    }
  }

  public set phxTooltipVisibleChange(value: EventEmitter<boolean>) {
    this._phxTooltipVisibleChange = value;
  }

  public set closeIconClicked(value: EventEmitter<void>) {
    this._closeIconClicked = value;
  }

  public get placement(): string {
    return this._placement || 'top';
  }

  public set size(value: PhxTooltipSize) {
    this._size = value;
  }
  public get size(): PhxTooltipSize {
    return this._size;
  }

  public set fixedWidth(value: PhxTooltipFixedWidth) {
    this._fixedWidth = value;
  }
  public get fixedWidth(): PhxTooltipFixedWidth {
    return this._fixedWidth;
  }

  public set maxWidth(value: number) {
    if (!value) return;

    if (value < PHX_MAX_WIDTH_MIN_VALUE) {
      this._maxWidth = PHX_MAX_WIDTH_MIN_VALUE;
      console.warn(`phx-tooltip: phxTooltipMaxWidth cannot be less than ${PHX_MAX_WIDTH_MIN_VALUE}`);
    } else if (value > PHX_MAX_WIDTH_MAX_VALUE) {
      this._maxWidth = PHX_MAX_WIDTH_MAX_VALUE;
      console.warn(`phx-tooltip: phxTooltipMaxWidth cannot be more than ${PHX_MAX_WIDTH_MAX_VALUE}`);
    } else {
      this._maxWidth = value;
    }
  }

  public get maxWidth(): number {
    return this._maxWidth;
  }

  public get maxWidthInPx(): string {
    return this._fixedWidth ? 'none' : `${this._maxWidth}px`;
  }

  public set actionsMaxHeight(value: number) {
    if (!value) return;

    this._actionsMaxHeight = value;
  }

  public get actionsMaxHeight(): number {
    return this._actionsMaxHeight;
  }

  public get actionsMaxHeightInPx(): string {
    return this._actionsMaxHeight ? `${this._actionsMaxHeight}px` : 'none';
  }

  public set intent(value: PhxTooltipIntent) {
    this._intent = value;
  }
  public get intent(): PhxTooltipIntent {
    return this._intent;
  }

  public set visible(value: boolean) {
    const visible = coerceBooleanProperty(value);
    if (this._visible !== visible) {
      this._visible = visible;
      this._phxTooltipVisibleChange.emit(visible);
    }
    if (this.onVisibilityChange) {
      this.onVisibilityChange.emit(visible);
    }
  }
  public get visible(): boolean {
    return this._visible;
  }

  public set isToggleTip(value: boolean) {
    this._isToggleTip = value;
  }

  public get isToggleTip(): boolean {
    return this._isToggleTip;
  }

  public set showBackdrop(value: boolean) {
    this._showBackdrop = value;
  }

  public get showBackdrop(): boolean {
    return this._showBackdrop && this._trigger === 'click';
  }

  public set trigger(value: PhxTriggerType) {
    this._trigger = value;
  }
  public get trigger(): PhxTriggerType {
    return this._trigger;
  }

  public set onVisibilityChange(visibilityChangeEventEmitter: EventEmitter<boolean>) {
    this._phxTooltipVisibleChange = visibilityChangeEventEmitter;
  }

  public get onVisibilityChange(): EventEmitter<boolean> {
    return this._phxTooltipVisibleChange;
  }

  public set showCloseIcon(value: boolean) {
    this._showCloseIcon = value;
  }

  public get showCloseIcon(): boolean {
    return this._showCloseIcon || (this._trigger === 'click' && !this._isToggleTip);
  }

  public set tooltipContext(value: TemplateRef<any>) {
    this._tooltipContext = value;
  }

  public get tooltipContext(): TemplateRef<any> {
    return this._tooltipContext;
  }

  public set offsetX(value: number) {
    if (this._offsetX !== value) {
      this._offsetX = value;
      this.updatePositions();
    }
  }

  public set offsetY(value: number) {
    if (this._offsetY !== value) {
      this._offsetY = value;
      this.updatePositions();
    }
  }

  public set paddingNone(value: boolean) {
    this._paddingNone = value;
  }

  public get paddingNone(): boolean {
    return this._paddingNone;
  }

  public set isToggleTipVisible(value: boolean) {
    this._isToggleTipVisible = value;
    if (value) {
      this.tooltipContent?.nativeElement?.focus();  // Focus the tooltip content when it is a toggle tip, doing this as by default pressing tab focuses the last action item instead of the first
    }
    this.cdr.detectChanges();
  }

  public get isToggleTipVisible(): boolean {
    return this._isToggleTipVisible;
  }

  public onOutsideClick(event: MouseEvent): void {
    if (this.visible && this.isToggleTipVisible) {
      this.outsideClick$.emit(event);
    }
  }

  public show(): void {
    const isEmpty = PhxTooltipComponent.isEmpty(this.title, this.subtitle, this.content);
    if (!this.visible && !isEmpty) {
      // Reset to the original placement preference when showing the tooltip
      if (this._placement !== this._originalPlacement) {
        this._placement = this._originalPlacement;
        this.updatePositions();
      }
      this.visible = true;
      this.cdr.detectChanges();
      
      this._toggleTipActions = this.tooltipContent?.nativeElement?.querySelector('[phxToggleTipActions]');
      if (this._toggleTipActions) {
        this._toggleTipActions.style.maxHeight = this.actionsMaxHeightInPx;
      }
    }
  }

  public hide(): void {
    if (this.visible) {
      this.visible = false;
      this.cdr.detectChanges();
    }
  }

  public updateTooltip(): void {
    this.setClassMap();
    this.updatePositions();
    this.cdr.detectChanges();
  }

  public updateToggleTooltipPosition(): void {
    this.overlay?.overlayRef?.updatePosition();
  }

  public setOverlayOrigin(origin: CdkOverlayOrigin | ElementRef<HTMLElement>): void {
    this.origin = origin instanceof ElementRef ? new CdkOverlayOrigin(origin) : origin;
    this.cdr.markForCheck();
  }

  public onPositionChange(position: ConnectedOverlayPositionChange): void {
    // Update only the current placement, not the original preference
    this._placement = getPlacementName(position);
    this.setClassMap();
    this.cdr.detectChanges();
  }

  public setMouseState(visible: boolean): void {
    this.mouseState$.next(visible);
  }

  public onCloseIconClick(): void {
    this.hide();
    this._closeIconClicked?.emit();
  }

  private updatePositions(): void {
    // Get the position for current placement
    const currentPosition = POSITION_MAP[this.placement];

    // Create base positions with current position first
    const basePositions = [
      currentPosition,
      ...DEFAULT_TOOLTIP_POSITIONS.filter(
        (pos) =>
          pos.originX !== currentPosition.originX ||
          pos.originY !== currentPosition.originY ||
          pos.overlayX !== currentPosition.overlayX ||
          pos.overlayY !== currentPosition.overlayY
      ),
    ];

    // Apply offset to all positions
    this.supportedPositions = basePositions.map((position) => ({
      ...position,
      offsetX: this._offsetX || 0,
      offsetY: this._offsetY || 0,
    }));

    // Update overlay position if it exists
    if (this.fixedWidth) this.classMap.set(`${this.prefixClass}-width-${this.fixedWidth}`, true);
  }

  private setClassMap(): void {
    this.classMap.clear();
    this.classMap.set(`${this.prefixClass}-${camelToSnakeCase(this.placement)}`, true);
    this.classMap.set(`${this.prefixClass}-${this.size}`, true);
    this.classMap.set(`${this.prefixClass}-${this.intent}`, true);
    if (this.overlayClassName) this.classMap.set(this.overlayClassName, true);
    if (this.fixedWidth) this.classMap.set(`${this.prefixClass}-width-${this.fixedWidth}`, true);
  }

  constructor(private cdr: ChangeDetectorRef, private el: ElementRef, private overlayService: Overlay) {
    this.scrollStrategy = this.overlayService.scrollStrategies.reposition();
  }
}
